import { ClientConfig } from '../../types/profile';

export interface GeneratedMetadataTags {
  title: string;
  description: string;
  favicon: string;
  appleTouchIcon?: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
  ogType: string;
  ogSiteName: string;
  themeColor: string;
}

/**
 * Resolves localized text or string for metadata tags.
 */
function resolveText(text: Record<string, string> | string | undefined, locale: string = 'ar'): string {
  if (!text) return '';
  if (typeof text === 'string') return text;
  return text[locale] || text['ar'] || text['en'] || Object.values(text)[0] || '';
}

/**
 * Extracts and prepares all metadata tags for a given ClientConfig.
 */
export function getClientMetadataTags(
  config: ClientConfig,
  locale: string = 'ar'
): GeneratedMetadataTags {
  const meta = config.metadata;
  const identity = config.identity;

  const title = meta?.title
    ? resolveText(meta.title, locale)
    : `${resolveText(identity.name, locale)} | Smart Digital Profile`;

  const description = meta?.description
    ? resolveText(meta.description, locale)
    : resolveText(identity.description, locale);

  const favicon = meta?.favicon || identity.logoUrl;
  const appleTouchIcon = meta?.appleTouchIcon || favicon;
  const canonicalUrl = meta?.canonicalUrl || config.profileUrl;

  const ogTitle = meta?.ogTitle
    ? resolveText(meta.ogTitle, locale)
    : title;

  const ogDescription = meta?.ogDescription
    ? resolveText(meta.ogDescription, locale)
    : description;

  const ogImage = meta?.ogImage || identity.logoUrl;
  const ogUrl = canonicalUrl;
  const ogType = meta?.ogType || 'profile';

  const ogSiteName = meta?.siteName
    ? resolveText(meta.siteName, locale)
    : resolveText(identity.name, locale);

  const themeColor = meta?.themeColor || config.branding.backgroundColor;

  return {
    title,
    description,
    favicon,
    appleTouchIcon,
    canonicalUrl,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    ogSiteName,
    themeColor,
  };
}

/**
 * Injects metadata into an HTML string for edge/worker responses.
 */
export function injectMetadataIntoHtml(
  html: string,
  config: ClientConfig,
  locale: string = 'ar'
): string {
  const tags = getClientMetadataTags(config, locale);

  let updated = html;

  // Replace <title>
  updated = updated.replace(/<title>.*?<\/title>/is, `<title>${escapeHtml(tags.title)}</title>`);

  // Replace or inject meta description
  if (updated.includes('<meta name="description"')) {
    updated = updated.replace(
      /<meta\s+name="description"\s+content=".*?"\s*\/?>/is,
      `<meta name="description" content="${escapeHtml(tags.description)}" />`
    );
  }

  // Replace or inject theme-color
  if (updated.includes('<meta name="theme-color"')) {
    updated = updated.replace(
      /<meta\s+name="theme-color"\s+content=".*?"\s*\/?>/is,
      `<meta name="theme-color" content="${escapeHtml(tags.themeColor)}" />`
    );
  }

  // Replace or inject canonical link
  if (updated.includes('<link rel="canonical"')) {
    updated = updated.replace(
      /<link\s+rel="canonical"\s+href=".*?"\s*\/?>/is,
      `<link rel="canonical" href="${escapeHtml(tags.canonicalUrl)}" />`
    );
  }

  // Replace or inject favicon
  if (updated.includes('<link rel="icon"')) {
    updated = updated.replace(
      /<link\s+rel="icon".*?href=".*?"\s*\/?>/is,
      `<link rel="icon" type="image/png" href="${escapeHtml(tags.favicon)}" />`
    );
  }

  // Replace Open Graph tags
  updated = updated.replace(
    /<meta\s+property="og:title"\s+content=".*?"\s*\/?>/is,
    `<meta property="og:title" content="${escapeHtml(tags.ogTitle)}" />`
  );

  updated = updated.replace(
    /<meta\s+property="og:description"\s+content=".*?"\s*\/?>/is,
    `<meta property="og:description" content="${escapeHtml(tags.ogDescription)}" />`
  );

  updated = updated.replace(
    /<meta\s+property="og:url"\s+content=".*?"\s*\/?>/is,
    `<meta property="og:url" content="${escapeHtml(tags.ogUrl)}" />`
  );

  updated = updated.replace(
    /<meta\s+property="og:image"\s+content=".*?"\s*\/?>/is,
    `<meta property="og:image" content="${escapeHtml(tags.ogImage)}" />`
  );

  // If og:site_name is missing, inject before </head>
  if (!updated.includes('property="og:site_name"')) {
    const ogSiteNameTag = `<meta property="og:site_name" content="${escapeHtml(tags.ogSiteName)}" />\n  `;
    updated = updated.replace('</head>', `  ${ogSiteNameTag}</head>`);
  } else {
    updated = updated.replace(
      /<meta\s+property="og:site_name"\s+content=".*?"\s*\/?>/is,
      `<meta property="og:site_name" content="${escapeHtml(tags.ogSiteName)}" />`
    );
  }

  return updated;
}

/**
 * Client-side DOM updater for SPA navigation
 */
export function updateClientDomMetadata(
  config: ClientConfig,
  locale: string = 'ar'
): void {
  if (typeof document === 'undefined') return;

  const tags = getClientMetadataTags(config, locale);

  // Update title
  document.title = tags.title;

  // Update description
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) metaDesc.setAttribute('content', tags.description);

  // Update theme-color
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) metaTheme.setAttribute('content', tags.themeColor);

  // Update canonical
  const linkCanonical = document.querySelector('link[rel="canonical"]');
  if (linkCanonical) linkCanonical.setAttribute('href', tags.canonicalUrl);

  // Update favicon
  let linkIcon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (linkIcon) {
    linkIcon.href = tags.favicon;
  } else {
    linkIcon = document.createElement('link');
    linkIcon.rel = 'icon';
    linkIcon.type = 'image/png';
    linkIcon.href = tags.favicon;
    document.head.appendChild(linkIcon);
  }

  // Update OG tags
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.setAttribute('content', tags.ogTitle);

  const ogDesc = document.querySelector('meta[property="og:description"]');
  if (ogDesc) ogDesc.setAttribute('content', tags.ogDescription);

  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) ogUrl.setAttribute('content', tags.ogUrl);

  const ogImage = document.querySelector('meta[property="og:image"]');
  if (ogImage) ogImage.setAttribute('content', tags.ogImage);

  let ogSiteName = document.querySelector('meta[property="og:site_name"]');
  if (ogSiteName) {
    ogSiteName.setAttribute('content', tags.ogSiteName);
  } else {
    ogSiteName = document.createElement('meta');
    ogSiteName.setAttribute('property', 'og:site_name');
    ogSiteName.setAttribute('content', tags.ogSiteName);
    document.head.appendChild(ogSiteName);
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
